/**
 * Cross-host SSO handoff. After login on the shared host (`app.maie.to`),
 * we redirect the user to their tenant's host (`slug.app.maie.to`) and
 * carry the just-issued tokens + identity in the URL fragment so the
 * tenant host can hydrate its auth store without another round-trip.
 *
 * Why fragment, not query:
 *   - Fragments aren't sent to the server in the request line.
 *   - History API replaceState scrubs them right after consumption.
 *
 * The access token is short-lived (15 min) and the refresh token is
 * single-use rotating, so the leak surface is small even if a screenshot
 * captures the URL.
 */

// Minimal shape of the auth store's BackendTokenResponse — duplicated
// here so this util doesn't import the store.
export interface SsoPayload {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive?: boolean;
    lastLogin?: string;
    createdAt?: string;
    updatedAt?: string;
    role: { id: string; name: string; permissions: Array<{ name: string }> };
  };
  tenant?: {
    id: string;
    slug: string;
    displayName: string;
    role?: string | null;
  };
  passwordExpired?: boolean;
}

const PARAM = 'p';
const NEXT_PARAM = 'next';

export function encodeSsoPayload(payload: SsoPayload): string {
  const json = JSON.stringify(payload);
  // Browser-safe UTF-8 → base64 (handles non-ASCII in display names).
  const utf8 = encodeURIComponent(json).replace(
    /%([0-9A-F]{2})/g,
    (_, hex) => String.fromCharCode(parseInt(hex, 16)),
  );
  return btoa(utf8);
}

export function decodeSsoPayload(encoded: string): SsoPayload | null {
  try {
    const utf8 = atob(encoded);
    const json = decodeURIComponent(
      utf8.split('').map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    );
    return JSON.parse(json) as SsoPayload;
  } catch {
    return null;
  }
}

/**
 * Build the `https://<slug>.<publicHostBase>[:port]/sso#p=...` URL.
 * Preserves protocol + port from the current location so dev (http +
 * :5173) and prod (https) both work.
 *
 * `nextPath` (optional) rides along in the fragment as `next=` and
 * tells the receiving SsoHandoffPage where to land after hydrating
 * auth — used by post-signup to land on /welcome instead of /home.
 */
export function buildTenantHandoffUrl(
  slug: string,
  publicHostBase: string,
  payload: SsoPayload,
  nextPath?: string,
): string {
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : '';
  const encoded = encodeSsoPayload(payload);
  const next = nextPath ? `&${NEXT_PARAM}=${encodeURIComponent(nextPath)}` : '';
  return `${protocol}//${slug}.${publicHostBase}${portSuffix}/sso#${PARAM}=${encodeURIComponent(encoded)}${next}`;
}

export function readSsoPayloadFromHash(): SsoPayload | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const encoded = params.get(PARAM);
  if (!encoded) return null;
  return decodeSsoPayload(encoded);
}

/**
 * The `next=` landing path carried alongside the SSO payload. Only
 * same-app absolute paths are honoured (`/x`, never `//host` or full
 * URLs) — anything else returns null and the caller falls back to
 * /home. Open-redirect guard, not a convenience.
 */
export function readSsoNextFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const next = new URLSearchParams(hash).get(NEXT_PARAM);
  if (!next) return null;
  if (!next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}

/** Replace the URL with `/<path>` and no hash, scrubbing the payload. */
export function consumeSsoHash(target: string = '/home'): void {
  window.history.replaceState({}, '', target);
}
